import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/documents";
import { CategoryIcon } from "../lib/fileIcons";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    BookCheck, Loader2, Search, ChevronLeft, ChevronRight, RotateCcw, ArrowRight,
    Send, Download, CheckCircle2, FileDown, Mail, ShieldCheck,
} from "lucide-react";

const ALL = "__all__";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const STATUS_BADGE = {
    read: { label: "LETTO", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    delivered: { label: "CONSEGNATO", cls: "bg-sky-100 text-sky-700", Icon: Download },
    sent: { label: "INVIATO", cls: "bg-amber-100 text-amber-700", Icon: Send },
};

export default function DeliveryRegisterPage() {
    const [q, setQ] = useState("");
    const [employeeId, setEmployeeId] = useState(ALL);
    const [category, setCategory] = useState(ALL);
    const [ackReq, setAckReq] = useState(ALL);
    const [status, setStatus] = useState(ALL);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [exporting, setExporting] = useState(false);
    const debounce = useRef(null);

    useEffect(() => {
        api.get("/employees").then((r) => {
            const list = [...r.data].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`));
            setEmployees(list);
        }).catch(() => {});
    }, []);

    const params = useMemo(() => {
        const p = { page, page_size: 50 };
        if (q.trim()) p.q = q.trim();
        if (employeeId !== ALL) p.employee_id = employeeId;
        if (category !== ALL) p.category = category;
        if (ackReq !== ALL) p.ack_required = ackReq;
        if (status !== ALL) p.status = status;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        return p;
    }, [q, employeeId, category, ackReq, status, dateFrom, dateTo, page]);

    useEffect(() => {
        clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data: res } = await api.get("/delivery-register/search", { params });
                setData(res);
            } catch (e) {
                toast.error("Errore nel caricamento del registro");
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => clearTimeout(debounce.current);
    }, [params]);

    const resetFilters = () => {
        setQ(""); setEmployeeId(ALL); setCategory(ALL); setAckReq(ALL);
        setStatus(ALL); setDateFrom(""); setDateTo(""); setPage(1);
    };
    const changeFilter = (setter) => (v) => { setter(v); setPage(1); };

    const exportPdf = async () => {
        setExporting(true);
        try {
            const { page: _p, page_size: _ps, ...rest } = params;
            const { data: blob } = await api.get("/delivery-register/export.pdf", {
                params: rest, responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `registro-consegne-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Registro PDF generato");
        } catch (e) {
            let msg = "Errore nella generazione del PDF";
            const d = e.response?.data;
            if (d instanceof Blob) {
                try { msg = JSON.parse(await d.text()).detail || msg; } catch { /* noop */ }
            } else if (typeof d?.detail === "string") {
                msg = d.detail;
            }
            toast.error(msg, { duration: 6000 });
        } finally {
            setExporting(false);
        }
    };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    const kpi = data?.kpi;

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="delivery-register-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <BookCheck className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Registro consegne</h1>
                        <p className="text-sm text-slate-500">
                            Certifica invio, consegna e lettura dei documenti inviati ai dipendenti
                        </p>
                    </div>
                </div>
                <Button onClick={exportPdf} disabled={exporting || loading || !data?.total}
                    className="gap-2 bg-[#15498F] text-white hover:bg-[#0f3a73]" data-testid="register-export-pdf-btn">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                    Esporta registro PDF
                </Button>
            </div>

            {kpi && (
                <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    <KpiCard testid="register-kpi-total" label="Documenti inviati" value={kpi.total} Icon={Send} color="text-slate-700" />
                    <KpiCard testid="register-kpi-delivered" label="Consegnati (scaricati)" value={kpi.delivered} Icon={Download} color="text-sky-700" />
                    <KpiCard testid="register-kpi-read" label="Presa visione confermata" value={kpi.read} Icon={ShieldCheck} color="text-emerald-700" />
                    <KpiCard testid="register-kpi-unread" label="Senza presa visione" value={kpi.unread} Icon={Mail} color="text-amber-700" />
                </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="relative min-w-[200px] flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Nome file o titolo…"
                        className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-700"
                        data-testid="register-search-input"
                    />
                </div>
                <Select value={employeeId} onValueChange={changeFilter(setEmployeeId)}>
                    <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="register-filter-employee">
                        <SelectValue placeholder="Dipendente" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value={ALL}>Tutti i dipendenti</SelectItem>
                        {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.cognome} {e.nome}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={category} onValueChange={changeFilter(setCategory)}>
                    <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="register-filter-category">
                        <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Tutte le categorie</SelectItem>
                        {CATEGORY_ORDER.map((c) => (
                            <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={changeFilter(setStatus)}>
                    <SelectTrigger className="h-9 w-[210px] text-sm" data-testid="register-filter-status">
                        <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Stato: tutti</SelectItem>
                        <SelectItem value="not_delivered">Inviati, non scaricati</SelectItem>
                        <SelectItem value="delivered">Consegnati (scaricati)</SelectItem>
                        <SelectItem value="delivered_not_read">Consegnati senza presa visione</SelectItem>
                        <SelectItem value="read">Letti (presa visione)</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={ackReq} onValueChange={changeFilter(setAckReq)}>
                    <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="register-filter-ack">
                        <SelectValue placeholder="Presa visione" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Presa visione: tutte</SelectItem>
                        <SelectItem value="required">Solo con presa visione richiesta</SelectItem>
                        <SelectItem value="not_required">Solo senza presa visione</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs text-slate-400">Dal</span>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" title="Inviati dal" data-testid="register-date-from" />
                    <span className="shrink-0 text-xs text-slate-400">al</span>
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" title="Inviati fino al" data-testid="register-date-to" />
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={resetFilters} title="Azzera filtri" data-testid="register-reset-filters">
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                </div>
            ) : !data || data.items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="register-empty">
                    Nessun documento trovato con i filtri selezionati.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <th className="px-3 py-2.5">Dipendente</th>
                                    <th className="px-3 py-2.5">Documento</th>
                                    <th className="px-3 py-2.5">Invio</th>
                                    <th className="px-3 py-2.5">Consegna</th>
                                    <th className="px-3 py-2.5">Lettura (eIDAS)</th>
                                    <th className="px-3 py-2.5 text-right">Stato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((r) => {
                                    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.sent;
                                    return (
                                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`register-row-${r.id}`}>
                                            <td className="whitespace-nowrap px-3 py-2 align-top">
                                                <Link to={`/dipendenti/${r.employee_id}`} className="flex items-center gap-1 font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">
                                                    {r.employee_name}
                                                    <ArrowRight className="h-3 w-3 text-slate-300" />
                                                </Link>
                                                {r.matricola && <div className="text-[11px] text-slate-400">Matr. {r.matricola}</div>}
                                            </td>
                                            <td className="max-w-xs px-3 py-2 align-top">
                                                <div className="flex items-center gap-2">
                                                    <CategoryIcon category={r.category} />
                                                    <div className="min-w-0">
                                                        <div className="truncate font-semibold text-slate-700">{r.title || r.filename}</div>
                                                        <div className="truncate text-[11px] text-slate-400">{CATEGORY_LABELS[r.category] || r.category}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 align-top text-xs text-slate-600">
                                                <div className="font-mono-numbers">{fmtDate(r.uploaded_at)}</div>
                                                {r.email_sent_at ? (
                                                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-teal-700">
                                                        <Mail className="h-3 w-3" /> Email {fmtDate(r.email_sent_at)}
                                                    </div>
                                                ) : (
                                                    <div className="text-[11px] text-slate-400">a disposizione</div>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                                                {r.delivered_at ? (
                                                    <>
                                                        <div className="font-mono-numbers font-semibold text-slate-700">{fmtDateTime(r.delivered_at)}</div>
                                                        {r.delivered_ip && <div className="text-[11px] text-slate-400">IP {r.delivered_ip}</div>}
                                                    </>
                                                ) : (
                                                    <span className="text-amber-600">non scaricato</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 align-top text-xs">
                                                {r.read_at ? (
                                                    <>
                                                        <div className="flex items-center gap-1 font-mono-numbers font-semibold text-emerald-700">
                                                            <ShieldCheck className="h-3.5 w-3.5" /> {fmtDateTime(r.read_at)}
                                                        </div>
                                                        {r.read_ip && <div className="text-[11px] text-slate-400">IP {r.read_ip}</div>}
                                                        {r.read_legacy && <div className="text-[10px] text-slate-400">portale precedente</div>}
                                                    </>
                                                ) : r.requires_acknowledgment ? (
                                                    <span className="text-amber-600">non ancora confermata</span>
                                                ) : (
                                                    <span className="text-slate-400">non richiesta</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-right align-top">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}>
                                                    <badge.Icon className="h-3 w-3" /> {badge.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span data-testid="register-pagination-info">
                            {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} di {data.total.toLocaleString("it-IT")}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="register-prev-page">
                                <ChevronLeft className="h-4 w-4" /> Prec.
                            </Button>
                            <span className="font-mono-numbers">pag. {data.page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="register-next-page">
                                Succ. <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function KpiCard({ label, value, Icon, color, testid }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testid}>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <Icon className={`h-3.5 w-3.5 ${color}`} /> {label}
            </div>
            <div className={`mt-1 font-mono-numbers text-2xl font-bold ${color}`}>{(value ?? 0).toLocaleString("it-IT")}</div>
        </div>
    );
}
